/**
 * The body of a home, list or hub route module (web plan W4.5): the existing
 * page, given the query answers the route's server loader read
 * (@/hooks/routePreload). The server renders the page's real content from
 * them (H1, breadcrumbs, the listed brands / offers / sales with their links,
 * the pagination), the browser hydrates the same render without asking again,
 * and a signed-in viewer's own copy (their favourites) loads after mount
 * (`useViewerState`). After an in-app navigation there is no server copy
 * (`clientLoader`): the page loads through Apollo as before.
 *
 * `catalogPath` (home, the lists, /nearby: the un-prefixed path of a
 * `catalog` route, @/routes/legacyRoutes): without a server answer (an in-app
 * navigation, `react-router dev`) the page follows the URL-move switch like
 * the SPA's routes did — an un-prefixed page after the move replaces the URL
 * with its `/{cc}/{lang}` one; before it, `/pk/en` replaces it with `/` and
 * the other prefixed catalog URLs of the default locale are no page (another
 * country's are its only catalog, switch or not). With a server answer the
 * server already decided (and redirected or 404'd), so the page renders.
 */
import { lazy, Suspense, useMemo, type ReactElement, type ReactNode } from 'react';
import { Navigate, useLoaderData, useLocation } from 'react-router';
import { RoutePreloadContext, type RoutePreload } from '@/hooks/routePreload';
import { useViewerState } from '@/hooks/useViewerState';
import { LEGACY_ROUTES, movedPathFor } from '@/routes/legacyRoutes';
import { DEFAULT_LOCALE } from '@/seo/locales';
import type { PageRouteData } from '@/seo/pageDocument';
import { LOCALE_ROUTES_LIVE, splitLocalePath } from '@/seo/urls';

// A client-side not-found state only (the server answers 404 itself): its own chunk, off the first load (audit F8).
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

/** Where the URL-move switch sends a catalog page rendered without a server answer, or null (see the file comment). */
function useCatalogMove(catalogPath: string | undefined, live: boolean): ReactElement | null {
  const { pathname, search, hash } = useLocation();
  if (catalogPath === undefined) return null;
  const split = splitLocalePath(pathname);
  if (split) {
    // Another country's catalog only exists under its prefix, whatever the switch says.
    if (live || split.locale.cc !== DEFAULT_LOCALE.cc) return null;
    return catalogPath === '/' ? <Navigate replace to={{ pathname: '/', search, hash }} /> : <NotFoundPage />;
  }
  const route = LEGACY_ROUTES.find((candidate) => candidate.catalog && candidate.path === catalogPath);
  const moved = route ? movedPathFor(route, live) : null;
  return moved ? <Navigate replace to={{ pathname: moved, search, hash }} /> : null;
}

export interface PageRouteProps {
  children: ReactNode;
  /** A catalog page's un-prefixed path (`/`, `/brands`, `/nearby`); hubs and sale pages have none. */
  catalogPath?: string;
  /** The URL-move switch (tests pass both states). */
  live?: boolean;
}

export function PageRoute({ children, catalogPath, live = LOCALE_ROUTES_LIVE }: PageRouteProps) {
  const loaded = useLoaderData() as PageRouteData | undefined;
  const { isSignedIn } = useViewerState();
  const queries = loaded?.preload ?? null;
  const preload = useMemo<RoutePreload | null>(
    () => (queries ? { queries, refresh: isSignedIn } : null),
    [queries, isSignedIn],
  );
  const move = useCatalogMove(catalogPath, live);
  if (move && !loaded?.head) return <Suspense fallback={null}>{move}</Suspense>;
  return (
    <RoutePreloadContext.Provider value={preload}>
      {/* A page's lazy parts (its client-side not-found state) suspend here, inside the shell. */}
      <Suspense fallback={<div className="min-h-page" aria-busy="true" />}>{children}</Suspense>
    </RoutePreloadContext.Provider>
  );
}

export default PageRoute;
